// FILE: fetch-dealers.js
// Helper function to normalize strings by removing non-alphanumeric characters and converting to lowercase
function handleize(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-$/, '')
    .replace(/^-/, '');
}

window.markers = [];
window.map = null;

// Constants
const OFFSET_INCREMENT = 0.005;
const CACHE_KEY = 'dealers_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

// Geocoding cache constants
const GEOCODE_CACHE_KEY = 'geocode_cache';
const GEOCODE_FAILED_CACHE_KEY = 'geocode_failed_cache';
const GEOCODE_CACHE_DURATION = 28 * 24 * 60 * 60 * 1000; // 28 days for successful geocodes
const FAILED_CACHE_TTL = 1 * 60 * 60 * 1000; // 1 hour for failed geocodes

// Geocoding cache utilities
function createGeocodeCacheKey(address) {
  return address.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getCachedGeocode(address) {
  try {
    const cacheKey = createGeocodeCacheKey(address);
    const cache = localStorage.getItem(GEOCODE_CACHE_KEY);
    if (!cache) return null;

    const geocodeCache = JSON.parse(cache);
    const cached = geocodeCache[cacheKey];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp < GEOCODE_CACHE_DURATION) {
      return cached.location;
    }
    return null;
  } catch (error) {
    return null;
  }
}

function setCachedGeocode(address, location) {
  try {
    const cacheKey = createGeocodeCacheKey(address);
    const cache = localStorage.getItem(GEOCODE_CACHE_KEY) || '{}';
    const geocodeCache = JSON.parse(cache);
    geocodeCache[cacheKey] = {
      location: {
        lat: typeof location.lat === 'function' ? location.lat() : location.lat,
        lng: typeof location.lng === 'function' ? location.lng() : location.lng
      },
      timestamp: Date.now()
    };
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(geocodeCache));
  } catch (error) {
    // Silently fail
  }
}

function setCachedGeocodeFailed(address) {
  try {
    const cacheKey = createGeocodeCacheKey(address);
    const cache = localStorage.getItem(GEOCODE_FAILED_CACHE_KEY) || '{}';
    const failedCache = JSON.parse(cache);
    failedCache[cacheKey] = Date.now();
    localStorage.setItem(GEOCODE_FAILED_CACHE_KEY, JSON.stringify(failedCache));
  } catch (error) {
    // Silently fail
  }
}

function isGeocodeFailed(address) {
  try {
    const cacheKey = createGeocodeCacheKey(address);
    const cache = localStorage.getItem(GEOCODE_FAILED_CACHE_KEY);
    if (!cache) return false;

    const failedCache = JSON.parse(cache);
    const failedTimestamp = failedCache[cacheKey];
    if (!failedTimestamp) return false;

    const now = Date.now();
    return (now - failedTimestamp) < FAILED_CACHE_TTL;
  } catch (error) {
    return false;
  }
}

// Add these helper functions after the handleize function
function getCachedDealers() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      // console.log('Using cached dealers data');
      return data;
    } else {
      // console.log('Cache expired, removing old data');
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  } catch (error) {
    console.error('Error reading cache:', error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function setCachedDealers(dealers) {
  try {
    const cacheData = {
      data: dealers,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    // console.log('Dealers data cached successfully');
  } catch (error) {
    console.error('Error caching data:', error);
  }
}

// Add this function to manually clear cache
function clearDealersCache() {
  localStorage.removeItem(CACHE_KEY);
  // console.log('Dealers cache cleared');
}

// Make it available globally for debugging
window.clearDealersCache = clearDealersCache;

// Add this more advanced caching function using Cache API
async function getCachedDealersAdvanced() {
  try {
    const cache = await caches.open('dealers-cache');
    const cachedResponse = await cache.match('dealers');

    if (cachedResponse) {
      const data = await cachedResponse.json();
      const timestamp = data.timestamp;
      const now = Date.now();

      if (now - timestamp < CACHE_DURATION) {
        // console.log('Using cached dealers data (Cache API)');
        return data.dealers;
      } else {
        // console.log('Cache expired, removing old data');
        await cache.delete('dealers');
      }
    }
    return null;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
}

async function setCachedDealersAdvanced(dealers) {
  try {
    const cache = await caches.open('dealers-cache');
    const cacheData = {
      dealers: dealers,
      timestamp: Date.now(),
    };

    const response = new Response(JSON.stringify(cacheData), {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await cache.put('dealers', response);
    // console.log('Dealers data cached successfully (Cache API)');
  } catch (error) {
    console.error('Error caching data:', error);
  }
}

// Helper function to fetch and parse SVG
async function fetchSVG(url) {
  try {
    const response = await fetch(url);
    const svgText = await response.text();
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    return svgDoc.querySelector('svg');
  } catch (error) {
    console.error('Error loading SVG:', error);
    return null;
  }
}

// Function to dynamically load Google Maps API script
async function loadGoogleMapsAPI() {
  // Check if already loaded
  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    return Promise.resolve();
  }

  // Get API key from map container data attribute
  const mapContainer = document.querySelector('.dealer-map-container');
  if (!mapContainer) {
    throw new Error('Map container not found');
  }
  
  // Try both camelCase formats (data-google-api-key becomes googleApiKey)
  const apiKey = mapContainer.dataset.googleApiKey || mapContainer.getAttribute('data-google-api-key');
  
  if (!apiKey) {
    throw new Error('Google Maps API key not found. Please check your theme settings.');
  }

  return new Promise((resolve, reject) => {
    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      
      // Timeout after 15 seconds
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (!window.google || !window.google.maps) {
          reject(new Error('Google Maps API failed to load (timeout waiting for existing script)'));
        }
      }, 15000);
      return;
    }

    // Use the Google Maps loader pattern (same as theme.liquid)
    // This creates the importLibrary function and loads the API
    const loaderCode = '(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({key:"' + apiKey + '",v:"weekly",libraries:"marker"});';
    
    // Create and execute the loader script
    const loaderScript = document.createElement('script');
    loaderScript.textContent = loaderCode;
    
    // Add script to head - it executes immediately
    document.head.appendChild(loaderScript);
    
    let attempts = 0;
    const maxAttempts = 150; // 15 seconds at 100ms intervals
    
    // Wait for the API to be ready
    const checkReady = setInterval(() => {
      attempts++;
      
      // Check if API is ready
      if (window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function') {
        clearInterval(checkReady);
        resolve();
        return;
      }
      
      // Timeout check
      if (attempts >= maxAttempts) {
        clearInterval(checkReady);
        console.error('[Maps API] Timeout waiting for API to load');
        console.error('[Maps API] window.google:', !!window.google);
        console.error('[Maps API] window.google.maps:', !!(window.google && window.google.maps));
        console.error('[Maps API] importLibrary:', !!(window.google && window.google.maps && window.google.maps.importLibrary));
        const mapsScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
        console.error('[Maps API] Maps script tag:', mapsScript ? mapsScript.src : 'not found');
        reject(new Error('Google Maps API failed to load within 15 seconds. Check console for details.'));
      }
    }, 100);
  });
}

// Extract all map loading logic into this function - only called when user clicks button or splash is disabled
async function loadMapAndMarkers() {
  const cardContainer = document.getElementById('company-cards-container');
  const proxyUrl = 'https://shopify-proxy-wranlger.cascadiamarquee.workers.dev/companies';

  try {
    const locateButton = document.getElementById('locate-button');
    if (locateButton) {
      locateButton.disabled = true;
      locateButton.classList.add('disabled');
    }

    const mapLoadingOverlay = document.createElement('div');
    mapLoadingOverlay.id = 'dealer-map-loading';
    mapLoadingOverlay.setAttribute('role', 'status');
    mapLoadingOverlay.setAttribute('aria-live', 'polite');
    mapLoadingOverlay.setAttribute('aria-label', 'Loading map');
    mapLoadingOverlay.innerHTML = `
      <div id="dealer-map-loading-animation">
        <svg width="80" height="80" viewBox="0 0 80 80" aria-hidden="true" focusable="false">
          <circle cx="40" cy="40" r="36" fill="none" stroke="#000" stroke-width="8" stroke-dasharray="170" stroke-dashoffset="20">
            <animate attributeName="stroke-dashoffset" values="360;0" dur="1.5s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
    `;

    const mapContainer = document.querySelector('.dealer-map-container');
    mapContainer.appendChild(mapLoadingOverlay);
    mapLoadingOverlay.style.display = 'flex';

    // Load Google Maps API dynamically (only when needed)
    await loadGoogleMapsAPI();

    await initMap();

    // Try to get cached data first
    let companies = getCachedDealers();

    if (!companies) {
      // console.log('Fetching fresh dealers data from API');
      // Fetch companies from API
      const response = await fetch(proxyUrl);
      companies = await response.json();

      // Cache the results
      setCachedDealers(companies);
    }

    if (companies.length > 0) {
      let index = 0;
      companies.forEach((company) => {
        const infoCard = createCompanyCard(company, index);
        cardContainer.appendChild(infoCard);
        index++;
      });

      initPopupEventHandlers();
      addEventListeners();

      await createMarkersFromInfoCards(companies);

      if (locateButton) {
        locateButton.disabled = false;
        locateButton.classList.remove('disabled');
      }

      mapLoadingOverlay.style.transition = 'opacity 0.5s ease-out';
      mapLoadingOverlay.style.opacity = '0';
      setTimeout(() => {
        mapLoadingOverlay.style.display = 'none';
      }, 500);
    } else {
      handleNoCompaniesFound(cardContainer);
    }
  } catch (error) {
    console.error('Error:', error);
    handleError(cardContainer);
  }
}

// Function to show the map splash screen
function showMapSplashScreen() {
  const mapContainer = document.querySelector('.dealer-map-container');
  if (!mapContainer) return;

  // Check if splash screen is enabled
  // Shopify outputs checkbox: checked = "true", unchecked = "false" (as strings in data attributes)
  // Default is true, so if undefined/empty, we show splash screen
  const showSplashScreenValue = mapContainer.dataset.showSplashScreen;
  // Remove any existing splash screen element (in case it was created before)
  const existingSplash = mapContainer.querySelector('#map-splash-screen');
  if (existingSplash) {
    existingSplash.remove();
  }
  
  // Only hide splash screen if explicitly set to "false"
  // Default behavior (undefined, empty, or "true") shows the splash screen
  if (showSplashScreenValue === 'false') {
    // Splash screen is explicitly disabled, load map immediately
    loadMapAndMarkers();
    return;
  }

  // Get settings from data attributes
  const splashTitle = mapContainer.dataset.splashTitle || 'Explore Dealer Locations';
  const splashDescription = mapContainer.dataset.splashDescription || 'Click below to view all Super Pacific dealers on an interactive map';
  const splashButtonText = mapContainer.dataset.splashButtonText || 'Load Map';
  const splashColorScheme = mapContainer.dataset.splashColorScheme || 'scheme-1';
  const splashBackgroundColor = mapContainer.dataset.splashBackgroundColor;
  const splashTextColor = mapContainer.dataset.splashTextColor;
  const splashBackgroundImage = mapContainer.dataset.splashBackgroundImage;
  const splashBackgroundSize = mapContainer.dataset.splashBackgroundSize || 'cover';
  const splashBackgroundPosition = mapContainer.dataset.splashBackgroundPosition || 'center';
  const splashBackgroundOverlayOpacity = parseFloat(mapContainer.dataset.splashBackgroundOverlayOpacity || '30') / 100;

  // Get color scheme CSS variables from the document root or scheme element
  const getColorSchemeValue = (property, fallback) => {
    // Try to get from a color scheme element first
    const schemeElement = document.querySelector(`.color-${splashColorScheme}`);
    if (schemeElement) {
      const computedStyle = window.getComputedStyle(schemeElement);
      const value = computedStyle.getPropertyValue(property).trim();
      if (value) return value;
    }
    // Fallback to document root
    const rootStyle = window.getComputedStyle(document.documentElement);
    const rootValue = rootStyle.getPropertyValue(property).trim();
    return rootValue || fallback;
  };

  // Add splash screen styles if not already added
  if (!document.getElementById('map-splash-styles')) {
    const style = document.createElement('style');
    style.id = 'map-splash-styles';
    style.textContent = `
      #map-splash-screen {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        transition: opacity 0.5s ease-out;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
      
      #map-splash-screen::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, var(--splash-overlay-opacity, 0));
        transition: background-color 0.3s ease;
        z-index: 1;
      }
      
      .map-splash-content {
        text-align: center;
        padding: 2rem;
        max-width: 500px;
        position: relative;
        z-index: 2;
      }
      
      .map-splash-icon {
        margin-bottom: 1.5rem;
        opacity: 0.9;
      }
      
      .map-splash-icon svg {
        width: 64px;
        height: 64px;
        filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
      }
      
      .map-splash-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0 0 1rem 0;
        text-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      .map-splash-button {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        position: relative;
      }
      
      .map-splash-button svg {
        transition: transform 0.3s ease;
      }
      
      .map-splash-button:hover svg {
        transform: translateX(4px);
      }
      
      #map-splash-screen.fade-out {
        opacity: 0;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }

  // Create splash screen overlay with dynamic styles
  const splashScreen = document.createElement('div');
  splashScreen.id = 'map-splash-screen';
  splashScreen.className = `color-${splashColorScheme}`;
  
  // Set background image if provided
  if (splashBackgroundImage) {
    splashScreen.style.backgroundImage = `url("${splashBackgroundImage}")`;
    splashScreen.style.backgroundSize = splashBackgroundSize;
    splashScreen.style.backgroundPosition = splashBackgroundPosition;
    
    // Apply overlay for text readability using CSS custom property
    splashScreen.style.setProperty('--splash-overlay-opacity', splashBackgroundOverlayOpacity);
    
    // If background image is set, don't use background color (image takes precedence)
    // But still allow text color override
    if (splashTextColor) {
      splashScreen.style.color = splashTextColor;
    }
  } else if (splashBackgroundColor) {
    // Set background color - use custom if provided, otherwise let color scheme handle it
    splashScreen.style.backgroundColor = splashBackgroundColor;
    splashScreen.style.color = splashTextColor || getColorSchemeValue('--color-foreground', 'rgb(var(--color-foreground))');
  }
  
  splashScreen.innerHTML = `
    <div class="map-splash-content">
      <div class="map-splash-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
        </svg>
      </div>
      <h2 id="map-splash-title" class="map-splash-title">${splashTitle}</h2>
      <div class="banner__text rte body">
      <p class="map-splash-description" id="map-splash-description">${splashDescription}</p>
      </div>
      <button type="button" id="load-map-button" class="button map-splash-button">
        <span>${splashButtonText}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  `;

  // Apply text color if custom color is set
  if (splashTextColor) {
    const content = splashScreen.querySelector('.map-splash-content');
    if (content) {
      content.style.color = splashTextColor;
      // Apply to title and description
      const title = content.querySelector('.map-splash-title');
      const description = content.querySelector('.map-splash-description');
      if (title) title.style.color = splashTextColor;
      if (description) description.style.color = splashTextColor;
    }
    const icon = splashScreen.querySelector('.map-splash-icon svg');
    if (icon) icon.style.color = splashTextColor;
  }

  splashScreen.setAttribute('role', 'dialog');
  splashScreen.setAttribute('aria-modal', 'true');
  splashScreen.setAttribute('aria-labelledby', 'map-splash-title');
  splashScreen.setAttribute('aria-describedby', 'map-splash-description');
  splashScreen.setAttribute('tabindex', '-1');
  mapContainer.prepend(splashScreen);

  Array.from(mapContainer.children).forEach((child) => {
    if (child !== splashScreen) child.setAttribute('inert', '');
  });

  // Add click handler to load map
  const loadButton = splashScreen.querySelector('#load-map-button');
  if (typeof trapFocus === 'function') {
    trapFocus(splashScreen, loadButton);
  } else {
    loadButton.focus();
  }
  loadButton.addEventListener('click', async () => {
    const zipInput = document.getElementById('locator-zip-code');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
    splashScreen.classList.add('fade-out');
    
    setTimeout(() => {
      Array.from(mapContainer.children).forEach((child) => {
        child.removeAttribute('inert');
      });
      splashScreen.remove();
      if (zipInput) zipInput.focus();
      loadMapAndMarkers();
    }, 500);
  });
}

// Show splash screen on page load - map only loads when user clicks button
document.addEventListener('DOMContentLoaded', function () {
  showMapSplashScreen();
});

async function initMap() {
  const { Map, RenderingType } = await google.maps.importLibrary('maps');
  await google.maps.importLibrary('marker');
  await google.maps.importLibrary('geometry');

  const mapElement = document.getElementById('dealer-map');
  mapElement.setAttribute('role', 'region');
  mapElement.setAttribute('aria-label', 'Map of dealer locations. Details for each location are listed beside the map.');
  map = new Map(mapElement, {
    center: { lat: 45.57336949417174, lng: -122.57614558668342 },
    zoom: 12,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    gestureHandling: 'greedy',
    minZoom: 4,
    renderingType: RenderingType.RASTER,
    mapId: '93d64d0ca3e6c232',
  });
  window.map = map;
  map.addListener('idle', () => {
    if (typeof updateCardsInMapBounds === 'function') updateCardsInMapBounds();
  });
}

/** =========================
 *  Address normalization + geocoding helpers
 *  ========================= */

// Rough US/CA tables so we can guess country if missing
const US_STATES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
]);
const CA_PROVINCES = new Set(['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']);

function normalizeAddressParts(addr) {
  let { address1, city, provinceCode, zip, countryCode } = addr || {};

  // Guess country if missing
  if (!countryCode) {
    if (provinceCode && US_STATES.has(provinceCode)) countryCode = 'US';
    else if (provinceCode && CA_PROVINCES.has(provinceCode)) countryCode = 'CA';
    else if (provinceCode === 'ENG') {
      // ENG isn't a Google-accepted country; use GB and expand text to "England"
      countryCode = 'GB';
      provinceCode = 'England';
    }
  }

  const parts = [address1, city, provinceCode, zip].filter(Boolean);
  const freeText = parts.join(', ');

  return { freeText, countryCode };
}

async function geocodeWithRetries(geocoder, freeText, countryCode) {
  if (!freeText) return null;

  // Check successful cache FIRST
  const cached = getCachedGeocode(freeText);
  if (cached) {
    return new google.maps.LatLng(cached.lat, cached.lng);
  }

  // Only skip if it failed recently AND we don't have a successful cache
  if (isGeocodeFailed(freeText)) {
    return null;
  }

  // 1) strict with country restriction
  let res = await tryGeocode(geocoder, freeText, countryCode);
  if (res) {
    setCachedGeocode(freeText, res);
    return res;
  }

  // 2) drop ZIP/postcode
  const noZip = freeText
    // remove trailing ", STATE ZIP..." or ", PROV POSTCODE..."
    .replace(/,\s*[A-Za-z]{2,}\s+[0-9A-Za-z -]+$/, '')
    // if that fails, also try removing last comma segment if it looks like a code
    .replace(/,\s*[0-9A-Za-z -]+$/, '');
  if (noZip && noZip !== freeText) {
    res = await tryGeocode(geocoder, noZip, countryCode);
    if (res) {
      setCachedGeocode(freeText, res);
      return res;
    }
  }

  // 3) drop province/state entirely
  const parts = freeText.split(',').map((s) => s.trim());
  if (parts.length > 2) {
    const noProvince = [parts[0], parts[1]].join(', ');
    res = await tryGeocode(geocoder, noProvince, countryCode);
    if (res) {
      setCachedGeocode(freeText, res);
      return res;
    }
  }

  // All attempts failed - cache the failure
  setCachedGeocodeFailed(freeText);
  return null;
}

async function tryGeocode(geocoder, address, countryCode) {
  try {
    const request = countryCode ? { address, componentRestrictions: { country: countryCode } } : { address };
    const results = await geocoder.geocode(request);
    const r0 = results?.results?.[0];
    return r0 ? r0.geometry.location : null;
  } catch (e) {
    // ZERO_RESULTS will land here in some SDKs; just return null to allow retries
    return null;
  }
}

/** =========================
 *  Marker creation
 *  ========================= */

async function createMarkersFromInfoCards(companies) {
  const bounds = new google.maps.LatLngBounds();
  const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();

  const mapContainer = document.querySelector('.dealer-map-container');
  const hqMarkerColor = mapContainer.dataset.hqMapMarkerColor;
  const dealerMarkerColor = mapContainer.dataset.dealerMapMarkerColor;
  const hqIconColor = mapContainer.dataset.hqMapMarkerIconColor;
  const dealerIconColor = mapContainer.dataset.dealerMapMarkerIconColor;

  window.markers.forEach((marker) => marker.setMap(null));
  window.markers = [];

  // --- HQ (if present) ---
  const hqCard = document.querySelector('.company-info-card[data-type="Super Pacific HQ"]');
  if (hqCard) {
    hqCard.setAttribute('data-card-id', 'hq_0');

    const hqAddressText = hqCard.getAttribute('data-address') || '';
    // Try to guess country from text; fallback none
    let hqCountry = null;
    if (/\b(usa|united states)\b/i.test(hqAddressText)) hqCountry = 'US';
    if (/\b(canada)\b/i.test(hqAddressText)) hqCountry = 'CA';
    if (/\b(uk|united kingdom|england)\b/i.test(hqAddressText)) hqCountry = 'GB';

    const hqLocation = await geocodeWithRetries(geocoder, hqAddressText, hqCountry);

    if (hqLocation) {
      const hqData = {
        title: hqCard.getAttribute('data-title'),
        address: hqAddressText,
        color: hqCard.getAttribute('data-color') || '#3b82f6',
        element: hqCard,
        location: hqLocation,
      };
      const markerContent = await createCustomMarker(hqData.title, hqMarkerColor, true, hqIconColor);
      const hqMarker = new AdvancedMarkerElement({
        position: hqLocation,
        map: window.map,
        title: hqData.title,
        content: markerContent,
        zIndex: 10,
      });
      hqMarker.cardId = 'hq_0';
      window.markers.push(hqMarker);
      bounds.extend(hqLocation);

      const hqInfoWindow = createInfoWindow(hqData);
      hqMarker.infoWindow = hqInfoWindow;

      markerContent.addEventListener('mouseenter', () => {
        // hqMarker.content.classList.add('hovered');
        closeAllInfoWindows();
        hqInfoWindow.open(window.map, hqMarker);
      });

      hqCard.addEventListener('click', () => {
        closeAllInfoWindows();
        window.map.setCenter(hqLocation);
        window.map.setZoom(10);
        hqInfoWindow.open(window.map, hqMarker);
      });

      hqCard.addEventListener('mouseenter', () => {
        hqMarker.infoWindow.open(window.map, hqMarker);
        // hqMarker.content.classList.add('hovered');
      });

      hqCard.addEventListener('mouseleave', () => {
        hqMarker.infoWindow.close();
        // hqMarker.content.classList.remove('hovered');
      });
    } else {
      console.warn('HQ address failed to geocode:', hqAddressText);
    }
  }

  // --- Dealers ---
  // Build normalized inputs (freeText + countryCode) for robust geocoding
  const companyAddressObjs = companies.map((company) => {
    return normalizeAddressParts({
      address1: company.address.address1,
      city: company.address.city,
      provinceCode: company.address.provinceCode,
      zip: company.address.zip,
      countryCode: company.address.countryCode, // if present in your data
    });
  });

  const locations = await batchGeocodeAddresses(companyAddressObjs);

  for (let i = 0; i < companies.length; i++) {
    const company = companies[i];
    const location = locations[i];
    if (!location) {
      const dbg = companyAddressObjs[i];
      console.warn(`No geocode for: ${dbg?.freeText} company: ${company.name} (country=${dbg?.countryCode || 'n/a'})`);
      continue;
    }

    const cardSelector = `.company-info-card[data-address="${company.address.address1}, ${company.address.city}, ${company.address.provinceCode} ${company.address.zip}"]`;
    const companyCard = document.querySelector(cardSelector);
    if (!companyCard) continue;

    const companyData = {
      title: companyCard.getAttribute('data-title'),
      address: companyCard.getAttribute('data-address'),
      phone: company.address.phone,
      email: company.email,
      website: company.website,
      element: companyCard,
      location: location,
    };

    const markerContent = await createCustomMarker(companyData.title, dealerMarkerColor, false, dealerIconColor);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: location,
      map: map,
      title: companyData.title,
      content: markerContent,
      zIndex: 1,
    });
    marker.cardId = companyCard.getAttribute('data-card-id');
    window.markers.push(marker);
    bounds.extend(location);

    const infoWindow = createInfoWindow(companyData);
    marker.infoWindow = infoWindow;

    companyCard.addEventListener('click', () => {
      closeAllInfoWindows();
      window.map.setCenter(location);
      window.map.setZoom(10);
      infoWindow.open(window.map, marker);
    });

    companyCard.addEventListener('mouseenter', () => {
      // if (marker.content) {
      //   marker.content.classList.add('hovered');
      // }
      closeAllInfoWindows();
      infoWindow.open(window.map, marker);
    });

    companyCard.addEventListener('mouseleave', () => {
      // if (marker.content) {
      //   marker.content.classList.remove('hovered');
      // }
      infoWindow.close();
    });

    if (marker.content) {
      marker.content.addEventListener('mouseenter', () => {
        // marker.content.classList.add('hovered');
        closeAllInfoWindows();
        infoWindow.open(window.map, marker);
      });
    }

    google.maps.event.addListener(infoWindow, 'domready', () => {
      const infoWindowContent = document.querySelector('.gm-style-iw');
      if (infoWindowContent) {
        // infoWindowContent.addEventListener('mouseenter', () => {
        //   // if (marker.content) marker.content.classList.add('hovered');
        // });
        infoWindowContent.addEventListener('mouseleave', () => {
          // if (marker.content) marker.content.classList.remove('hovered');
          infoWindow.close();
        });
      }
    });

    google.maps.event.addListener(marker, 'click', () => {
      closeAllInfoWindows();
      window.map.setCenter(location);
      window.map.setZoom(10);
      infoWindow.open(window.map, marker);
    });
  }

  if (typeof fitMapToMarkers === 'function') {
    fitMapToMarkers(bounds);
  } else {
    window.map.fitBounds(bounds);
  }
  const event = new CustomEvent('infoCardsLoaded');
  document.dispatchEvent(event);
}

// Concurrency-limited batch geocode using the new normalization + retries
async function batchGeocodeAddresses(addressObjs) {
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();
  const CONCURRENCY = 5;
  const out = new Array(addressObjs.length);

  let i = 0;
  async function worker() {
    while (i < addressObjs.length) {
      const idx = i++;
      const obj = addressObjs[idx];
      out[idx] = await geocodeWithRetries(geocoder, obj?.freeText, obj?.countryCode);
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return out;
}

/** (kept for HQ path but unused elsewhere) legacy single-address geocode with retries */
async function geocodeAddress(address, countryCode = null) {
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();
  try {
    return await geocodeWithRetries(geocoder, address, countryCode);
  } catch (error) {
    console.error('Geocode error for', address, ':', error);
    return null;
  }
}

async function createCustomMarker(element, color, isHQ = false, iconColor = '#FFFFFF') {
  const markerElement = document.createElement('div');
  markerElement.classList.add('marker');
  // markerElement.style.backgroundColor = color;

  if (isHQ) {
    markerElement.style.zIndex = '10';
  }

  const iconContainer = document.createElement('div');
  iconContainer.classList.add('company-marker');
  iconContainer.style.backgroundColor = color;
  iconContainer.style.boxShadow = `2px 1px 8px 0px rgba(0,0,0,0.4)`;

  const mapContainer = document.querySelector('.dealer-map-container');
  let iconUrl;

  if (isHQ) {
    iconUrl = mapContainer.dataset.hqMapMarkerIcon;
  } else {
    iconUrl = mapContainer.dataset.ownerMapMarkerIcon;
  }

  if (iconUrl && (iconUrl.startsWith('http') || iconUrl.startsWith('/'))) {
    try {
      const svg = await fetchSVG(iconUrl);
      if (svg) {
        svg.setAttribute('width', '16');
        svg.setAttribute('height', '16');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

        const gElement = svg.querySelector('g');
        if (gElement) {
          gElement.removeAttribute('transform');
        }

        const paths = svg.querySelectorAll('path');
        paths.forEach((path) => {
          path.setAttribute('fill', iconColor);
        });

        svg.style.cssText = 'max-width: 100%; max-height: 100%; display: block; margin: auto;';

        iconContainer.appendChild(svg);
        markerElement.appendChild(iconContainer);
        return markerElement;
      }
    } catch (error) {
      console.warn('Failed to load custom SVG, using fallback:', error);
    }
  }

  // Fallback icon
  iconContainer.innerHTML = `<svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.1365 7.55595L8.75441 7.52795L8.70255 0.227817C8.70255 0.00381279 8.43748 -0.0861889 8.32607 0.101815C6.38222 3.37388 1.52259 10.44 0.531453 12.114C0.445017 12.26 0.54682 12.444 0.713929 12.444H6.24776L6.1498 19.7722C6.1498 19.9962 6.41487 20.0862 6.52628 19.8982C8.44516 16.6681 13.299 9.60599 14.3132 7.89596C14.4016 7.74596 14.3017 7.55396 14.1384 7.55396L14.1365 7.55595Z" fill="${iconColor}"/>
  </svg>`;

  markerElement.appendChild(iconContainer);
  return markerElement;
}

// Close all InfoWindows by iterating through markers
function closeAllInfoWindows() {
  if (window.markers) {
    window.markers.forEach((marker) => {
      if (marker && marker.infoWindow && typeof marker.infoWindow.close === 'function') {
        marker.infoWindow.close();
      }
    });
  }
}

// Handle company card click
function handleCompanyCardClick(company, marker, infoWindow) {
  closeAllInfoWindows();
  map.setCenter(company.location);
  map.setZoom(10);
  infoWindow.open(map, marker);
}

// Helper function to create a company card with a unique index
function createCompanyCard(company, index) {
  const card = document.createElement('div');
  const decryptedEmail = atob(company.metafields.find((meta) => meta.key === 'company_email')?.value || '');
  const formattedPhone = company.address.phone.replace(/(\+1)(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');

  card.classList.add('card', 'company-info-card');

  const name = company.name || '';
  const email = decryptedEmail;
  const address = company.address.address1 || '';
  const city = company.address.city || '';
  const state = company.address.provinceCode || '';
  const zip = company.address.zip || '';
  const phone = formattedPhone;
  const website = company.metafields.find((meta) => meta.key === 'website')?.value || '';
  const displayWebsite = website.replace(/^https?:\/\//, '').replace(/\/$/, '');

  let servicesList = [];
  if (company.metafields) {
    const servicesMetafield = company.metafields.find((meta) => meta.key === 'tags');
    if (servicesMetafield) {
      try {
        servicesList = JSON.parse(servicesMetafield.value);
      } catch (e) {
        console.warn('Error parsing services:', e);
        servicesList = [];
      }
    }
  }
  const tagsHtml =
    servicesList.length > 0
      ? `<div class="card--tags">
        ${servicesList.map((tag) => `<span class="card--tag">${tag}</span>`).join('')}
       </div>`
      : '';

  card.innerHTML = `
    <div class="card--header">
      <div class="card--company-header-wrapper">
        <h3 class="card--company-name">${name}</h3>
        ${tagsHtml}
      </div>
    </div>
    <div class="card--body">
      <div class="card--row-wrapper">
        <p class="card--address">
          <span class="card--company-address-line">${address}</span>
         <br>
         <span class="card--company-address-line">${city}, ${state} ${zip}</span>
        </p>
      </div>
      <div class="card--row-wrapper">
        <div class="card--contact-details">
            <a class="link link--text card--phone" href="tel:${phone}">${phone}</a>
            <a class="link link--text card--email" href="mailto:${email}">${email}</a>
            <a class="link link--text card--website" href="${website}" target="_blank">${displayWebsite}</a>
        </div>
      </div>
    </div>
  `;

  const servicesString = servicesList.join(',');
  card.setAttribute('data-services', servicesString);
  card.setAttribute('data-address', `${address}, ${city}, ${state} ${zip}`);
  card.setAttribute('data-title', `${name}`);
  card.setAttribute('data-name', `${name}`);
  card.setAttribute('data-email', `${email}`);
  card.setAttribute('data-phone', `${phone}`);
  card.setAttribute('data-type', `${name}`);
  card.setAttribute('data-website', `${website}`);
  card.setAttribute('data-filter', servicesString);

  const uniqueId = `dealer_${index}`;
  card.setAttribute('data-card-id', uniqueId);

  return card;
}

function createInfoWindow(data) {
  const infoWindow = new google.maps.InfoWindow({
    content: getInfoWindowContent(data),
    disableAutoPan: true,
  });
  return infoWindow;
}

function getInfoWindowContent(data) {
  const phone = data.element?.getAttribute('data-phone');
  const email = data.element?.getAttribute('data-email');
  const website = data.element?.getAttribute('data-website');

  const addressParts = data.address?.split(',') || [];
  const streetAddress = addressParts[0] || '';
  const cityStateZip = addressParts.slice(1).join(',').trim();

  let contentString = `
    <div class="info-window dealer">
      <div class="info-title"><strong>${data.title}</strong></div>
      <div>
        <div class="info-address-wrapper">
          <div>${streetAddress}</div>
          <div>${cityStateZip}</div>
        </div>
        <div class="info-contact-wrapper">
          ${phone ? `<div><a href="tel:${phone}">${phone}</a></div>` : ''}
          ${email ? `<div><a href="mailto:${email}">${email}</a></div>` : ''}
          ${website ? `<div><a href="${website}" target="_blank">${website}</a></div>` : ''}
        </div>
      </div>
      <div class="info-body">
  `;

  if (data.location) {
    const distanceText =
      !isNaN(data.distance) && isFinite(data.distance) ? `${(data.distance * 0.000621371).toFixed(2)} Mi.` : '';
    contentString += createDirectionsSection(data.location, distanceText);
  }

  contentString += `</div></div>`;
  return contentString;
}

function createDirectionsSection(location, distanceText) {
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

  return `
    <hr>
    <div>
      <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" 
         target="_blank" 
         id="get-directions" 
         class="info-window-footer">
        <div>Directions</div>
        <div>
          <span class="icon arrow-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
              <path fill="currentColor" d="M352 128c0-17.7-14.3-32-32-32L96 96c-17.7 0-32 14.3-32 32s14.3 32 32 32l146.7 0L41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L288 205.3 288 352c0 17.7 14.3 32 32 32s32-14.3 32-32l0-224z"/>
            </svg>
          </span>
          ${distanceText}
        </div>
      </a>
    </div>
  `;
}

// Function to add event listeners after the info-cards are loaded
function addEventListeners() {
  const dealerListWrapper = document.querySelector('.dealer-list-wrapper'); // Fix the selector
  const stickyHandle = document.querySelector('.sticky-handle-wrapper');
  const companyInfoCards = document.querySelectorAll('.company-info-card');
  const vehicleModelSelector = document.getElementById('vehicle-model-selector');
  const mapWrapper = document.querySelector('.map-wrapper'); // Assuming this is the map container

  if (!stickyHandle) {
    console.error('stickyHandle not found');
    return;
  }

  // Function to check if we're in mobile view
  function isMobileWidth() {
    return window.innerWidth <= 768; // Mobile width threshold
  }

  // Click behavior for expanding/collapsing the dealer list (only if mobile width)
  stickyHandle.addEventListener('click', () => {
    if (isMobileWidth()) {
      // console.log('Sticky handle clicked (mobile)');
      toggleExpandedClass();
    } else {
      // console.log('Sticky handle clicked but not mobile');
    }
  });

  // Remove the "expanded" class when clicking on any company-info-card (only if mobile width)
  companyInfoCards.forEach((card) => {
    card.addEventListener('click', () => {
      if (isMobileWidth()) {
        toggleExpandedClass();
        resetChildScrollPositions();
      }
    });
  });

  // Ensure vehicle model selector click doesn't trigger expanding
  if (vehicleModelSelector) {
    vehicleModelSelector.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  // Reset scroll positions of all child elements
  function resetChildScrollPositions() {
    dealerListWrapper.querySelectorAll('*').forEach((child) => {
      child.scrollTop = 0;
      child.scrollLeft = 0;
    });
  }

  // Toggle the expanded class
  function toggleExpandedClass() {
    // console.log('toggleExpandedClass initialized');

    dealerListWrapper.classList.toggle('expanded'); // Toggle the expanded class

    // console.log('expanded class status:', dealerListWrapper.classList.contains('expanded'));
  }

  // Event listener to handle window resizing
  window.addEventListener('resize', () => {
    // console.log('Window resized. Is mobile?', isMobileWidth());
  });
}

function handleError(cardContainer) {
  cardContainer.innerHTML = `
    <div class="error-message" role="alert">
      <p>Sorry, we encountered an error loading the dealer locations.</p>
      <p>Please try refreshing the page or contact support if the problem persists.</p>
    </div>
  `;
}
